import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Users } from "lucide-react";
import { api, formatInt } from "@/lib/api";
import { MetricLabel, QualityNotes } from "@/lib/metrics";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

// K1 — friend data source labels (CSV 匯入 > LINE 官方每日統計（暫估）; webhook no longer counted; data ends yesterday)
export const md = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}` : "—");

export function friendSourceText(c) {
  if (!c) return null;
  const csv = c.csv_days || 0, est = c.insight_days || 0, man = c.manual_days || 0, miss = c.missing_days || 0;
  const u = (c.n_properties || 1) > 1 ? "場域日" : "天";
  return [`CSV 匯入 ${csv} ${u}`, `官方暫估 ${est} ${u}`, ...(man ? [`手動 ${man} ${u}`] : []), `尚未有資料 ${miss} ${u}`].join(" + ");
}

export function FriendSourceLabel({ c, testid = "friend-source-label", className = "" }) {
  if (!c) return null;
  return (
    <div className={`text-[11px] text-slate-500 flex flex-wrap gap-x-2 ${className}`} data-testid={testid}>
      <span data-testid={`${testid}-days`}>{friendSourceText(c)}</span>
      <span data-testid={`${testid}-import`}>· 最後匯入 {md((c.csv_last_imported_at || "").slice(0, 10))} · 涵蓋到 {md(c.csv_covered_until)}</span>
      {c.last_data_date && <span data-testid={`${testid}-until`}>· 資料截至 {md(c.last_data_date)}</span>}
      {(c.insight_days || 0) > 0 && <span className="text-amber-700">· 暫估日路徑：尚未匯入</span>}
    </div>
  );
}

// ⚠️ lines when the range is not fully covered
export function friendCoverageNotes(c) {
  if (!c) return [];
  const out = [];
  const tot = (c.csv_days || 0) + (c.insight_days || 0) + (c.manual_days || 0) + (c.missing_days || 0);
  if (c.missing_days > 0) out.push(`有 ${c.missing_days} ${(c.n_properties || 1) > 1 ? "場域日" : "天"}沒有好友資料（共 ${tot}），數字會偏低 → 匯入路徑 CSV`);
  if (c.insight_days > 0) out.push(`${c.insight_days} ${(c.n_properties || 1) > 1 ? "場域日" : "天"}為 LINE 官方每日統計暫估（T+1，沒有路徑明細），匯入 CSV 後會自動改用 CSV`);
  if (c.today_excluded && c.data_until) out.push(`好友資料只到 ${c.data_until}（今天不計）`);
  return out;
}

// sums per-property coverage objects into one (for table headers)
export function sumCoverage(list) {
  const cs = list.filter(Boolean);
  if (!cs.length) return null;
  const keys = ["csv_days", "insight_days", "manual_days", "missing_days"];
  const o = Object.fromEntries(keys.map((k) => [k, cs.reduce((a, c) => a + (c[k] || 0), 0)]));
  return { ...o, n_properties: cs.length, today_excluded: cs.some((c) => c.today_excluded), data_until: cs.map((c) => c.data_until).filter(Boolean).sort().pop(), last_data_date: cs.map((c) => c.last_data_date).filter(Boolean).sort().pop(),
    csv_covered_until: cs.map((c) => c.csv_covered_until).filter(Boolean).sort()[0], csv_last_imported_at: cs.map((c) => c.csv_last_imported_at).filter(Boolean).sort().pop() };
}

export function FriendCoverageHover({ c, children, testid }) {
  if (!c) return children;
  const notes = friendCoverageNotes(c);
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <span className={`cursor-help ${notes.length ? "underline decoration-dotted decoration-amber-500" : ""}`} data-testid={testid}>{children}</span>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="end" className="w-auto bg-white border border-gray-200 text-slate-700 text-xs p-2.5 rounded-md shadow-xl space-y-1" data-testid={testid ? `${testid}-card` : undefined}>
        <div className="font-semibold text-slate-800">好友資料來源</div>
        <FriendSourceLabel c={c} testid={testid ? `${testid}-src` : "friend-cov-src"} />
        <QualityNotes notes={notes} testid={testid ? `${testid}-notes` : undefined} />
      </HoverCardContent>
    </HoverCard>
  );
}

let _statusPromise = null;
let _statusAt = 0;
export function useFriendStatus(propertyId) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let off = false;
    const key = propertyId && propertyId !== "all" ? propertyId : "all";
    const p = key === "all"
      ? ((_statusPromise && Date.now() - _statusAt < 60000) ? _statusPromise : (_statusAt = Date.now(), _statusPromise = api.get("/line/friends/status").then((r) => r.data)))
      : api.get("/line/friends/status", { params: { property_id: key } }).then((r) => r.data);
    p.then((x) => { if (!off) setD(x); }).catch(() => { if (!off) setD({ rows: [] }); });
    return () => { off = true; };
  }, [propertyId]);
  return d;
}

export function EffectiveFriendsCard({ propertyId, testid = "effective-friends" }) {
  const d = useFriendStatus(propertyId);
  if (!d) return null;
  const rows = d.rows.filter((r) => r.effective_friends != null);
  const total = rows.reduce((a, r) => a + r.effective_friends, 0);
  const asOf = rows.map((r) => r.as_of).sort()[0];
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <MetricLabel label="目前有效好友數" hintKey="effective_friends" testid={`${testid}-info`} className="text-xs font-medium text-slate-500"
          notes={rows.length === 0 ? ["還沒有 LINE 官方統計：需要場域設定好 Messaging API，系統每天 10:30 抓前一天的數字"] : rows.length < d.rows.length ? [`只有 ${rows.length}/${d.rows.length} 個場域有官方統計`] : undefined} />
        <Users className="h-3.5 w-3.5 text-teal-600" />
      </div>
      <div className="mt-1 text-2xl font-bold font-mono tabular-nums text-slate-900" data-testid={`${testid}-value`}>{rows.length ? formatInt(total) : "—"}</div>
      <div className="mt-0.5 text-[10px] text-slate-500" data-testid={`${testid}-asof`}>{rows.length ? `LINE 官方統計 · 資料截至 ${md(asOf)}` : "LINE 官方統計 · 尚無資料"}</div>
      {propertyId === "all" && rows.length > 1 && (
        <details className="mt-1 text-[11px] text-slate-600"><summary className="cursor-pointer text-slate-500">各場域</summary>
          {rows.map((r) => <div key={r.property_id} className="flex justify-between font-mono" data-testid={`${testid}-row-${r.property_id}`}><span className="font-sans">{r.name}</span><span>{formatInt(r.effective_friends)}（{md(r.as_of)}）</span></div>)}
        </details>
      )}
    </div>
  );
}

// 總覽: one compact chip; full detail lives in settings › 加好友管道 (#csv-status)
export function CsvReminderBanner({ propertyId, testid = "csv-reminder", short = false }) {
  const d = useFriendStatus(propertyId);
  const nav = useNavigate();
  if (!d) return null;
  const late = d.rows.filter((r) => r.csv_reminder);
  if (!late.length) return null;
  return (
    <HoverCard openDelay={100} closeDelay={80}>
      <HoverCardTrigger asChild>
        <Link to="/settings/line-sources#csv-status" data-testid={testid} onClick={() => nav("/settings/line-sources#csv-status")}
          className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] text-amber-800 hover:bg-amber-100">
          <AlertTriangle className="h-3 w-3" />{short ? `${late.length} 個場域待匯入 →` : `${late.length} 個場域路徑 CSV 待匯入 → 前往匯入`}
        </Link>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" className="w-auto max-w-[260px] bg-white border border-gray-200 text-xs text-slate-700 p-2 shadow-xl" data-testid={`${testid}-card`}>
        {late.map((r) => <div key={r.property_id} data-testid={`${testid}-${r.property_id}`}>{r.name}</div>)}
      </HoverCardContent>
    </HoverCard>
  );
}
